import React from 'react';
import { replaceStrValues } from 'platform/shared/health-apps';
import {
  textUI,
  radioUI,
  radioSchema,
  currencyUI,
  currencySchema,
  currentOrPastDateUI,
  currentOrPastDateSchema,
  titleUI,
  arrayBuilderItemFirstPageTitleUI,
  arrayBuilderItemSubsequentPageTitleUI,
  arrayBuilderYesNoSchema,
  arrayBuilderYesNoUI,
} from 'platform/forms-system/src/js/web-component-patterns';
import VaComboBoxField from 'platform/forms-system/src/js/web-component-fields/VaComboBoxField';
import { arrayBuilderPages } from '~/platform/forms-system/src/js/patterns/array-builder';
import {
  medicalExpenseRecipientLabels,
  frequencyLabels,
  medicalExpenseTypeLabels,
} from '../../../../utils/labels';
import { customTextSchema } from '../../../definitions';
import {
  transformDate,
  formatCurrency,
  shouldSkipExpensePages,
} from '../../../../utils/helpers';
import content from '../../../../utils/content.json';

const allRecipientTypes = {
  ...medicalExpenseRecipientLabels,
};

const filterRecipients = fullData => {
  const keys = Object.keys(allRecipientTypes);
  const hasDependentChildren =
    !!fullData?.veteranChildrenCount && fullData.veteranChildrenCount !== '0';
  return hasDependentChildren ? keys : keys.filter(key => key !== 'CHILD');
};

function introDescription() {
  return (
    <div>
      <p className="vads-u-margin-top--0">
        We’ll now ask about medical or certain other expenses that aren’t
        reimbursed. You may add up to 6 medical or other expenses.
      </p>
      <p>These types of expenses can include:</p>
      <ul>
        <li>
          <span className="vads-u-font-weight--bold">
            Recurring medical expenses{' '}
          </span>
          that insurance doesn’t cover
        </li>
        <li>
          <span className="vads-u-font-weight--bold">
            One-time medical expenses{' '}
          </span>
          that insurance doesn’t cover that occurred after you started this form
          or after you submitted an Intent to File
        </li>
        <li>
          <span className="vads-u-font-weight--bold">
            Last or burial expenses{' '}
          </span>
          that you paid related to the last illness or burial of the Veteran
        </li>
      </ul>
      <p>
        <span className="vads-u-font-weight--bold">Note: </span>
        We use your expenses to lower the income we count toward your claim. If
        you have no income to report, you do not need to list any expenses.
      </p>

      <va-additional-info trigger="How to report monthly recurring expenses">
        <p>
          For recurring monthly expenses, report them as one expense. Include
          the start date and how much you pay each month or year.
        </p>
        <p>Start a new expense if:</p>
        <ul>
          <li>The amount you pay changed</li>
          <li>The expense ended (list as separate, individual expenses)</li>
        </ul>
        <p>
          <strong>Note: </strong>
          Prescription medications are generally not considered recurring.
        </p>
      </va-additional-info>
    </div>
  );
}

/** @type {ArrayBuilderOptions} */
export const options = {
  arrayPath: 'medicalExpenses',
  nounSingular: content['financial-medical-noun-singular'],
  nounPlural: content['financial-medical-noun-plural'],
  required: false,
  maxItems: 6,
  isItemIncomplete: item =>
    !item?.recipient ||
    (['CHILD'].includes(item?.recipient) && !item?.childName) ||
    !Object.keys(medicalExpenseTypeLabels).includes(item?.purpose) ||
    !item?.paymentDate ||
    !item?.paymentFrequency ||
    !item?.provider ||
    !item?.paymentAmount,
  text: {
    cancelAddTitle: replaceStrValues(
      content['array-cancel-add-title'],
      `${content['financial-medical-noun-singular']}?`,
    ),
    cancelEditTitle: replaceStrValues(
      content['array-cancel-edit-title'],
      `${content['financial-medical-noun-singular']}?`,
    ),
    cancelAddDescription: content['financial-medical-cancel-add-description'],
    cancelAddReviewDescription:
      content['financial-medical-cancel-add-description'],
    cancelEditDescription: content['financial-medical-cancel-edit-description'],
    cancelEditReviewDescription:
      content['financial-medical-cancel-edit-description'],
    cancelAddYes: content['array-cancel-add-yes'],
    cancelAddNo: content['array-cancel-add-no'],
    cancelEditYes: content['array-cancel-edit-yes'],
    cancelEditNo: content['array-cancel-edit-no'],
    deleteDescription: replaceStrValues(content['array-delete-description'], [
      `${content['financial-medical-noun-plural']}.`,
      `${content['financial-medical-noun-singular']}.`,
    ]),
    deleteNeedAtLeastOneDescription: content['array-empty-hint'],
    deleteNo: content['array-delete-no'],
    deleteTitle: replaceStrValues(
      content['array-delete-title'],
      `${content['financial-medical-noun-singular']}?`,
    ),
    deleteYes: content['array-delete-yes'],
    alertMaxItems: (
      <div>
        <p className="vads-u-margin-top--0">
          You have added the maximum number of allowed medical, last, burial, or
          other expenses for this application. Additional expenses can be added
          using a Medical Expense Report (VA Form 21P-8416).
        </p>
        <p>
          You can submit this form online, upload it at the end of this
          application, or send it to us by mail.
        </p>
        <span className="vads-u-display--block vads-u-margin-bottom--2">
          <va-link
            href="https://www.va.gov/supporting-forms-for-claims/submit-medical-expense-report-form-21p-8416"
            external
            text="Submit medical expenses to support a pension or parents’ D.I.C. claim"
          />
        </span>
        <span className="vads-u-display--block">
          <va-link
            href="https://www.va.gov/find-forms/about-form-21p-8416"
            external
            text="Get VA Form 21P-8416 to download"
          />
        </span>
      </div>
    ),
    getItemName: (item, index) =>
      `Expense ${index + 1}: ${item?.provider || 'No provider name'}`,
    cardDescription: item => (
      <div>
        <span className="vads-u-display--block">
          {transformDate(item?.paymentDate) || 'Date not provided'}
        </span>
        <span className="vads-u-display--block">
          {frequencyLabels[item?.paymentFrequency] || 'Frequency not provided'}
        </span>
        <span className="vads-u-display--block">
          {formatCurrency(item?.paymentAmount) || 'Amount not provided'}
        </span>
      </div>
    ),
    summaryTitle: content['financial-medical-summary-title'],
  },
};

const introPage = {
  uiSchema: {
    ...titleUI({
      title: content['financial-medical-intro-title'],
      nounSingular: options.nounSingular,
      nounPlural: options.nounPlural,
    }),
    'ui:description': introDescription,
  },
  schema: {
    type: 'object',
    properties: {},
  },
};

const summaryPage = {
  uiSchema: {
    'view:medicalExpensesList': arrayBuilderYesNoUI(
      options,
      {
        title: content['financial-medical-summary-add'],
        hint: content['array-empty-hint'],
      },
      {
        title: content['financial-medical-summary-add-another'],
        hint: content['array-empty-hint'],
      },
    ),
  },
  schema: {
    type: 'object',
    properties: {
      'view:medicalExpensesList': arrayBuilderYesNoSchema,
    },
    required: ['view:medicalExpensesList'],
  },
};

const recipientPage = {
  uiSchema: {
    ...arrayBuilderItemFirstPageTitleUI({
      title: content['financial-medical-recipient-title'],
      nounSingular: options.nounSingular,
      nounPlural: options.nounPlural,
    }),
    recipient: {
      ...radioUI({
        title: content['financial-medical-recipient-question'],
      }),
      'ui:options': {
        updateSchema: (
          _itemData,
          schema,
          _uiSchema,
          _index,
          _path,
          fullData,
        ) => {
          const recipients = filterRecipients(fullData);
          return {
            ...schema,
            enum: recipients,
            enumNames: recipients.map(key => allRecipientTypes[key]),
          };
        },
      },
    },
    childName: textUI({
      title: content['financial-medical-recipient-child-name'],
      maxLength: 256,
      expandUnder: 'recipient',
      expandUnderCondition: field => field === 'CHILD',
      required: (formData, index, fullData) => {
        const items = formData?.medicalExpenses ?? fullData?.medicalExpenses;
        const item = items?.[index];
        return ['CHILD'].includes(item?.recipient);
      },
      errorMessages: {
        required: content['financial-medical-recipient-child-name-error'],
      },
    }),
    provider: textUI({
      title: content['financial-medical-recipient-provider'],
      hint: content['financial-medical-recipient-provider-hint'],
      'ui:required': true,
      errorMessages: {
        required: content['financial-medical-recipient-provider-error'],
      },
    }),
  },
  schema: {
    type: 'object',
    properties: {
      recipient: radioSchema(Object.keys(medicalExpenseRecipientLabels)),
      childName: customTextSchema,
      provider: customTextSchema,
    },
    required: ['recipient', 'provider'],
  },
};

const purposeDatePage = {
  uiSchema: {
    ...arrayBuilderItemSubsequentPageTitleUI(
      content['financial-medical-purpose-title'],
    ),
    purpose: {
      'ui:title': content['financial-medical-purpose-question'],
      'ui:webComponentField': VaComboBoxField,
      'ui:errorMessages': {
        required: content['financial-medical-purpose-question-error'],
      },
      'ui:options': {
        labels: medicalExpenseTypeLabels,
      },
      'ui:confirmationField': ({ formData }) => ({
        data: formData.label,
        label: content['financial-medical-purpose-question'],
      }),
    },
    paymentDate: currentOrPastDateUI({
      title: content['financial-medical-purpose-date'],
      monthSelect: false,
    }),
  },
  schema: {
    type: 'object',
    properties: {
      purpose: {
        type: 'string',
        enum: Object.keys(medicalExpenseTypeLabels),
      },
      paymentDate: currentOrPastDateSchema,
    },
    required: ['purpose', 'paymentDate'],
  },
};

const frequencyCostPage = {
  uiSchema: {
    ...arrayBuilderItemSubsequentPageTitleUI(
      content['financial-medical-frequency-title'],
    ),
    paymentFrequency: radioUI({
      title: content['financial-medical-frequency-question'],
      labels: frequencyLabels,
    }),
    paymentAmount: currencyUI({
      title: content['financial-medical-frequency-amount'],
      max: 999999999.0,
    }),
  },
  schema: {
    type: 'object',
    properties: {
      paymentFrequency: radioSchema(Object.keys(frequencyLabels)),
      paymentAmount: currencySchema,
    },
    required: ['paymentFrequency', 'paymentAmount'],
  },
};

// Should show when the following are true:
// 2022: Always show.
// 2025: If survivors pension is not checked, show pages.
// 2025: If survivors pension is checked, only show pages if incomeSourcesCount is not 'NO_INCOME'.
export const medicalExpensesPages = arrayBuilderPages(options, pageBuilder => ({
  medicalExpensesIntro: pageBuilder.introPage({
    title: content['financial-medical-intro-title'],
    path: 'financial-information/medical-expenses',
    depends: formData => !shouldSkipExpensePages(formData),
    uiSchema: introPage.uiSchema,
    schema: introPage.schema,
  }),
  medicalExpensesSummary: pageBuilder.summaryPage({
    title: content['financial-medical-summary-title'],
    path: 'financial-information/medical-expenses/add',
    depends: formData => !shouldSkipExpensePages(formData),
    uiSchema: summaryPage.uiSchema,
    schema: summaryPage.schema,
  }),
  medicalRecipientPage: pageBuilder.itemPage({
    title: content['financial-medical-recipient-title'],
    path: 'financial-information/medical-expenses/:index/recipient-provider',
    depends: formData => !shouldSkipExpensePages(formData),
    uiSchema: recipientPage.uiSchema,
    schema: recipientPage.schema,
  }),
  medicalPurposeDatePage: pageBuilder.itemPage({
    title: content['financial-medical-purpose-title'],
    path: 'financial-information/medical-expenses/:index/purpose-date',
    depends: formData => !shouldSkipExpensePages(formData),
    uiSchema: purposeDatePage.uiSchema,
    schema: purposeDatePage.schema,
  }),
  medicalFrequencyCostPage: pageBuilder.itemPage({
    title: content['financial-medical-frequency-title'],
    path: 'financial-information/medical-expenses/:index/frequency-cost',
    depends: formData => !shouldSkipExpensePages(formData),
    uiSchema: frequencyCostPage.uiSchema,
    schema: frequencyCostPage.schema,
  }),
}));

export default medicalExpensesPages;
